import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

// Convertit l'attribut JSON `style` (composants, cellules de layout.grid) en objet React
// `{ prop: val }` — accepte soit déjà un objet (utilisé tel quel), soit une chaîne façon attribut
// HTML natif (`"color: red; font-weight: bold;"`, kebab-case → camelCase). Sans cette conversion,
// passer directement une chaîne à `style={{ ...style }}` la décompose caractère par caractère
// (`{0:'c', 1:'o', ...}`) — React tente alors d'assigner ces clés numériques sur
// `CSSStyleDeclaration`, ce que le navigateur rejette ("Indexed property setter is not supported").
// `null`/`undefined`/chaîne vide → `undefined` (rien à fusionner).
export function toStyleObject(style) {
  if (style == null || style === '') return undefined
  if (typeof style !== 'string') return style
  const result = {}
  for (const decl of style.split(';')) {
    const idx = decl.indexOf(':')
    if (idx === -1) continue
    const prop = decl.slice(0, idx).trim()
    const value = decl.slice(idx + 1).trim()
    if (!prop || !value) continue
    // Custom properties (--foo) conservées telles quelles — jamais camelCase en CSS.
    result[prop.startsWith('--') ? prop : prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = value
  }
  return result
}

// crypto.randomUUID() n'existe qu'en contexte sécurisé (HTTPS ou localhost) — indisponible en HTTP
// simple, fréquent pour l'application cible (intranet/recette). Repli sur un UUID v4 généré
// manuellement, suffisant ici (identifiants techniques non cryptographiques : clé React, id de ligne
// temporaire, id d'iframe).
export function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// z-index "toujours au-dessus" pour les overlays transitoires portalés dans document.body (ex.
// dropdown Radix Select d'un ComboField/PromptCombo) : ces popovers doivent rester visibles même
// rendus à l'intérieur d'une popup tbsUserMessages, dont le zIndex est alloué dynamiquement et
// croît à chaque ouverture (voir UserMessages.jsx, BASE_Z_INDEX) — un palier fixe modeste (ex.
// 10002) finirait par repasser derrière après suffisamment d'ouvertures de popups dans la session.
export const OVERLAY_Z_INDEX = 999999

// Chrome/Edge/Safari masquent purement et simplement l'icône calendrier native d'un
// `<input type="date">` (`::-webkit-calendar-picker-indicator`) en dessous d'un certain seuil de
// largeur (~120px, confirmé par test) — aucune règle CSS sur l'icône elle-même ne permet de le
// contourner. Sur ces moteurs, tout champ/cellule de type Date la masque (CSS `.tbs-date-input`,
// `index.css`) et la remplace par un bouton propre (même principe que le trigger de
// réinitialisation) qui ouvre le picker natif via `showPicker()` — voir ChampField.jsx (mc=104),
// TableField.jsx/TreeField.jsx (colonnes `Date`). Sans objet sous Firefox (icône native déjà
// correcte, non masquée) — calculé une seule fois au chargement du module, pas par rendu.
// `CSS.supports('selector(::-webkit-calendar-picker-indicator)')`, PAS
// `CSS.supports('-webkit-appearance', 'none')` : Firefox reconnaît aussi cette dernière propriété
// (alias de compat ajouté pour le web, sans rapport avec le moteur de rendu réel), ce qui
// déclencherait à tort le bouton custom ET laisserait l'icône native visible en double (vérifié
// par test) — seul le test de support du *sélecteur* du pseudo-élément est spécifique à
// Chrome/Edge/Safari.
export const HAS_WEBKIT_DATE_ICON = typeof CSS !== 'undefined' && CSS.supports('selector(::-webkit-calendar-picker-indicator)')

// Conversion dates : format externe dd/mm/yyyy ↔ format HTML ISO yyyy-mm-dd
export const toIso   = v => (v && /^\d{2}\/\d{2}\/\d{4}$/.test(v)) ? `${v.slice(6)}-${v.slice(3,5)}-${v.slice(0,2)}` : (v ?? '')
export const fromIso = v => (v && /^\d{4}-\d{2}-\d{2}$/.test(v))   ? `${v.slice(8)}/${v.slice(5,7)}/${v.slice(0,4)}` : (v ?? '')

// Reproduit $.fn.html(value) de jQuery : injection HTML directe (innerHTML) dans le cas général,
// mais parsing + ré-exécution réelle des <script> embarqués (inline ou src) si la valeur contient
// <script>/<style>/<link> — cas que `innerHTML`/`dangerouslySetInnerHTML` ne couvre jamais (un
// <script> inséré ainsi ne s'exécute jamais, contrainte du navigateur, pas de React). Un <template>
// sert de parseur inerte (mêmes règles HTML que le DOM réel, sans exécuter les scripts) ; chaque
// <script> rencontré est ensuite remplacé par un clone fraîchement créé (attributs + contenu), un
// script ajouté via `appendChild` s'exécutant, contrairement à un script parsé depuis de l'innerHTML.
const RE_HTML_NEEDS_SCRIPT_EXEC = /<(?:script|style|link)/i

export function setHtmlLikeJQuery(el, html) {
  if (!el) return
  const value = html ?? ''
  if (!RE_HTML_NEEDS_SCRIPT_EXEC.test(value)) {
    el.innerHTML = value
    return
  }
  el.innerHTML = ''
  const template = document.createElement('template')
  template.innerHTML = value
  for (const node of [...template.content.childNodes]) {
    if (node.nodeType === 1 && node.tagName === 'SCRIPT') {
      const script = document.createElement('script')
      for (const attr of node.attributes) script.setAttribute(attr.name, attr.value)
      script.textContent = node.textContent
      el.appendChild(script)
    } else {
      el.appendChild(node)
    }
  }
}

// Dépouille une chaîne HTML de ses balises (texte brut résultant) — pour les contextes qui ne
// peuvent pas rendre de HTML (attribut `title`, `<option>`, libellé de checkbox en clair) mais
// reçoivent une valeur par ailleurs traitée comme du HTML (ex. `label` de colonne, voir
// `setColumnLabel` dans TableField.jsx/TreeField.jsx). Un `<div>` inerte (jamais inséré dans le
// document) sert de parseur ; `textContent` renvoie alors le texte déjà dépouillé de ses balises,
// même principe que le tooltip de LabelField.
export function stripHtml(html) {
  if (html == null) return ''
  const div = document.createElement('div')
  div.innerHTML = String(html)
  return div.textContent ?? ''
}

// Appelle un callback global fourni par le code métier (JSP) en piégeant les exceptions,
// qui sont journalisées dans la console plutôt que de faire planter le rendu React.
export function safeInvoke(fn, ...args) {
  if (typeof fn !== 'function') return undefined
  try {
    return fn(...args)
  } catch (e) {
    console.error(e)
    return undefined
  }
}

// Normalise un attribut `fixedHeight` (entier, chaîne numérique sans unité → pixels ; chaîne
// avec unité CSS explicite comme "150px"/"10em" → conservée telle quelle).
export function toCssLength(value) {
  if (value == null || value === '') return undefined
  if (typeof value === 'number') return `${value}px`
  const str = String(value).trim()
  return /^-?\d+(\.\d+)?$/.test(str) ? `${str}px` : str
}

// Tri numérique pour les colonnes Numerique/Montant — parseFloat() tolère aussi bien les valeurs
// déjà flottantes (normalizeCellValue) qu'un éventuel résidu chaîne, et place les valeurs
// invalides/absentes (NaN) en dernier plutôt que de casser le tri (sortingFns.basic de TanStack
// ferait des comparaisons NaN systématiquement fausses, ordre alors non déterministe).
export function numericSortingFn(rowA, rowB, columnId) {
  const a = parseFloat(rowA.getValue(columnId))
  const b = parseFloat(rowB.getValue(columnId))
  if (isNaN(a) && isNaN(b)) return 0
  if (isNaN(a)) return -1
  if (isNaN(b)) return 1
  return a - b
}

// Contrat onTbsGridLoader/onTbsTreeLoader/getJson() : les valeurs de ligne sont toujours des chaînes
// (ou null pour une valeur absente), jamais des nombres/booléens bruts.
export function toJsonStr(v) {
  return (v === null || v === undefined) ? null : String(v)
}

// Valeur considérée vraie pour une colonne Select (types 15/16) ou une checkbox (type 2, mc=115) :
// booléen `true`, ou `1`/`"1"`/`"true"` (entier ou chaîne tolérés en plus du booléen/de la chaîne
// "true" — contrat commun aux deux composants).
export function isSelectTrue(v) {
  return v === true || v === 'true' || v === '1' || v === 1
}

const pad2 = n => String(n).padStart(2, '0')
// `<input type="date"/"datetime-local">` exige une année sur exactement 4 chiffres dans l'attribut
// `value` (format `yyyy-MM-dd`) — un `d.getFullYear()` non paddé (ex. "2" pour l'an 2) produit une
// chaîne hors format ("2-10-11") que le navigateur rejette silencieusement, réinitialisant alors
// TOUS les segments du champ (jour/mois y compris), pas seulement l'année. Se produit en pratique
// dès la 1ère frappe d'une nouvelle année dans un champ contrôlé : Chrome expose déjà une valeur
// intermédiaire zero-paddée ("0002-10-11", cf. makeLocalDate ci-dessus) qui repart aussitôt dans le
// `value` React une fois reformatée par dateToIsoInput/dateTimeToIsoInput.
const pad4 = n => String(n).padStart(4, '0')

// `new Date(year, ...)` interprète un `year` entre 0 et 99 comme 1900+year (ex. `new Date(2, 0, 1)`
// → l'an 1902, pas l'an 2) — piège classique du constructeur JS, `setFullYear()` après coup est le
// seul moyen de l'écraser. Nécessaire ici : tant que l'utilisateur n'a tapé qu'un ou deux chiffres
// de l'année dans un `<input type="date">` contrôlé, Chrome expose déjà un `.value` avec l'année
// zero-paddée (ex. "0002"), et ce round-trip repart aussitôt dans le `value` React (isoInputToDate
// → dateToIsoInput) — sans ce correctif, le champ affiche "1902" à la place et écrase la saisie en
// cours au lieu de laisser l'utilisateur continuer à taper.
function makeLocalDate(year, month, day, h = 0, mi = 0, s = 0) {
  const d = new Date(year, month, day, h, mi, s)
  if (year >= 0 && year <= 99) d.setFullYear(year)
  return d
}

// Parsing "JJ/MM/AAAA" → Date locale (minuit) ; format invalide → Invalid Date (jamais d'exception,
// cohérent avec le contrat "get() rend toujours une Date" — isNaN(d.getTime()) signale l'erreur).
function parseFrDate(v) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(v).trim())
  return m ? makeLocalDate(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : new Date(NaN)
}

// Parsing "JJ/MM/AAAA HH24:mm:ss" ou "JJ/MM/AAAA" (heure → minuit) → Date locale.
function parseFrDateTime(v) {
  const s = String(v).trim()
  let m = /^(\d{2})\/(\d{2})\/(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(s)
  if (m) return makeLocalDate(Number(m[3]), Number(m[2]) - 1, Number(m[1]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0))
  m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s)
  return m ? makeLocalDate(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : new Date(NaN)
}

// Formatage Date → "JJ/MM/AAAA" (getJson()/affichage) ; Date invalide/non-Date → null.
export function formatFrDate(d) {
  return (d instanceof Date && !isNaN(d.getTime()))
    ? `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`
    : null
}

// Formatage Date → "JJ/MM/AAAA HH24:mm:ss" (getJson()/affichage) ; Date invalide/non-Date → null.
export function formatFrDateTime(d) {
  return (d instanceof Date && !isNaN(d.getTime()))
    ? `${formatFrDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
    : null
}

// Coercition générique (indépendante d'une colonne type 15/16) — objet Date JS ↔ chaîne "JJ/MM/AAAA".
// `null`/`undefined`/`''` → null (valeur absente) ; Date déjà instanciée → renvoyée telle quelle
// (idempotent) ; chaîne non parsable → Invalid Date (jamais null pour une chaîne non vide, cohérent
// avec le contrat "getValue() rend toujours une Date").
export function coerceDate(v) {
  if (v == null || v === '') return null
  return v instanceof Date ? v : parseFrDate(v)
}

// Coercition générique — flottant JS ↔ nombre/chaîne convertible. `null`/`undefined`/`''` → null.
export function coerceFloat(v) {
  if (v == null || v === '') return null
  return typeof v === 'number' ? v : parseFloat(v)
}

// Coercition générique — entier JS ↔ nombre/chaîne convertible. `null`/`undefined`/`''` → null.
export function coerceInt(v) {
  if (v == null || v === '') return null
  return typeof v === 'number' ? v : parseInt(v, 10)
}

// Conversions entier (minutes depuis minuit, 0-1439 — type 2 mc=106) ↔ valeur d'un
// <input type="time"> ("HH:MM").
export function minutesToTimeInput(m) {
  if (typeof m !== 'number' || isNaN(m)) return ''
  const h = Math.floor(m / 60) % 24
  const mm = ((m % 60) + 60) % 60
  return `${pad2(h)}:${pad2(mm)}`
}
export function timeInputToMinutes(s) {
  const m = /^(\d{2}):(\d{2})$/.exec(s ?? '')
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

// Conversions Date ↔ valeur d'un <input type="date"> ("yyyy-mm-dd").
export function dateToIsoInput(d) {
  return (d instanceof Date && !isNaN(d.getTime()))
    ? `${pad4(d.getFullYear())}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
    : ''
}
export function isoInputToDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  return m ? makeLocalDate(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}

// Conversions Date ↔ valeur d'un <input type="datetime-local"> ("yyyy-mm-ddTHH:mm:ss").
export function dateTimeToIsoInput(d) {
  return (d instanceof Date && !isNaN(d.getTime()))
    ? `${pad4(d.getFullYear())}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
    : ''
}
export function isoInputToDateTime(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(s)
  return m ? makeLocalDate(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0)) : null
}

// Normalise la valeur stockée en cellule (rowsRef/nodesRef) selon le type de colonne (types 15/16) :
// Select → booléen ; Date/DateTime → objet Date JS (Invalid Date si non parsable, jamais null pour
// une chaîne non vide) ; Heure → entier ; Numerique/Montant → flottant. Chaîne vide/null/undefined →
// null (valeur absente). Autres types (Texte, Combo...) : valeur inchangée. À appliquer à chaque
// écriture en cellule (chargement onTbsGridLoader/onTbsTreeLoader, addRow/addNode, setCellValue, édition
// de cellule, set() du proxy de ligne/nœud) ; idempotent (une valeur déjà du type cible est renvoyée
// telle quelle).
export function normalizeCellValue(col, v) {
  switch (col?.type) {
    // Contrairement aux autres types, Select n'a pas d'état "absent" distinct de "décoché" — une
    // valeur manquante coerce donc en `false`, jamais en `null` (cohérent avec "get() rend toujours
    // un booléen", sans exception, contrairement à Date/Heure/Numerique/Montant).
    case 'Select':
      return isSelectTrue(v)
    case 'Date':
      return coerceDate(v)
    case 'DateTime':
      if (v == null || v === '') return null
      return v instanceof Date ? v : parseFrDateTime(v)
    case 'Heure':
      return coerceInt(v)
    case 'Numerique':
    case 'Montant':
      return coerceFloat(v)
    default:
      return v
  }
}

// Compare deux valeurs de cellule déjà normalisées (normalizeCellValue) selon le type de colonne —
// utilisé pour n'émettre onCellEdit/onTreeGridRowEdit que si l'édition a réellement changé la
// donnée (ex. focus + blur sans frappe, ou re-sélection du même item de Combo). Date/DateTime :
// comparaison par valeur (`getTime()`), pas par référence (chaque édition recrée un nouvel objet
// Date) ; deux Invalid Date sont considérées égales (NaN !== NaN sinon). Autres types : `===`,
// suffisant pour des primitives (string/number/boolean/null).
export function cellValuesEqual(col, a, b) {
  if ((col?.type === 'Date' || col?.type === 'DateTime') && a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime() || (isNaN(a.getTime()) && isNaN(b.getTime()))
  }
  return a === b
}

// Sérialisation d'une valeur de cellule (getJson()) selon le type de colonne : Select → "true"/
// "false" (jamais null — même exception que normalizeCellValue), Date/DateTime → "JJ/MM/AAAA"[
// HH24:mm:ss], autres types → toJsonStr() (contrat "toujours une chaîne, ou null si absente").
export function cellToJsonStr(col, v) {
  if (col?.type === 'Select') return isSelectTrue(v) ? 'true' : 'false'
  if (v == null) return null
  switch (col?.type) {
    case 'Date':     return formatFrDate(v)
    case 'DateTime': return formatFrDateTime(v)
    // Un objet File brut donnerait "[object File]" via toJsonStr() — seul son nom est sérialisable.
    case 'File':     return v?.name ? v.name : toJsonStr(v)
    default:         return toJsonStr(v)
  }
}

// Position du drop (drag & drop de lignes) relative à la ligne survolée, déduite de la
// position verticale du curseur. `isLeaf:false` ouvre une zone centrale ('append') pour déposer
// comme enfant — n'a de sens que pour un nœud d'arbre non-feuille.
export function computeDropPosition(e, isLeaf) {
  const rect = e.currentTarget.getBoundingClientRect()
  const ratio = (e.clientY - rect.top) / rect.height
  if (isLeaf) return ratio < 0.5 ? 'before' : 'after'
  if (ratio < 0.25) return 'before'
  if (ratio > 0.75) return 'after'
  return 'append'
}
